/**
 * ROADMAP JOURNAL MANAGER
 * 
 * Admin component for managing roadmap journal entries:
 * - Create, edit, and delete journal entries
 * - Upload multiple photos and videos with drag & drop
 * - Preview entries before publishing
 * - Reorder entries and media
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RoadmapJournalService } from '../../lib/roadmapJournalService';
import { AnnouncementBuilder, type BuilderElement } from './AnnouncementBuilder';
import type {
  RoadmapJournalEntry,
  RoadmapJournalMedia,
  MediaUploadProgress,
  JournalStatistics,
  CreateJournalEntryRequest
} from '../../types/roadmapJournal';

// ============ TYPES ============
interface EntryFormData {
  title: string;
  description: string;
  entry_date: string;
  is_published: boolean;
  blocks: BuilderElement[];
}

const defaultFormData: EntryFormData = {
  title: '',
  description: '',
  entry_date: new Date().toISOString().split('T')[0],
  is_published: false,
  blocks: []
};

function blocksForEntry(entry?: RoadmapJournalEntry): BuilderElement[] {
  if (Array.isArray(entry?.blocks) && entry.blocks.length) return entry.blocks as BuilderElement[];
  if (entry?.description) return [{ type: 'text', text: entry.description }];
  return [];
}

const MediaUploader: React.FC<{
  entryId: string;
  onMediaAdded: () => void;
}> = ({ entryId, onMediaAdded }) => {
  const [uploads, setUploads] = useState<MediaUploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      const isVideo = file.type.startsWith('video/');
      const isPhoto = file.type.startsWith('image/');
      
      if (!isVideo && !isPhoto) {
        alert(`Unsupported file type: ${file.name}`);
        continue;
      }

      const uploadProgress: MediaUploadProgress = {
        file,
        progress: 0,
        status: 'uploading'
      };

      setUploads(prev => [...prev, uploadProgress]);

      try {
        if (isPhoto) {
          await RoadmapJournalService.uploadPhoto(file, entryId);
        } else {
          await RoadmapJournalService.uploadVideo(file, entryId, (progress) => {
            setUploads(prev => prev.map(u => 
              u.file === file ? { ...u, progress } : u
            ));
          });
        }

        setUploads(prev => prev.map(u => 
          u.file === file ? { ...u, status: 'complete', progress: 100 } : u
        ));

        setTimeout(() => {
          setUploads(prev => prev.filter(u => u.file !== file));
          onMediaAdded();
        }, 1500);
      } catch (error) {
        setUploads(prev => prev.map(u => 
          u.file === file ? { 
            ...u, 
            status: 'error', 
            error: error instanceof Error ? error.message : 'Upload failed' 
          } : u
        ));
      }
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [entryId]);

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging ? 'border-yellow-400 bg-yellow-400/10' : 'border-gray-600 hover:border-gray-500'
        }`}
      >
        <div className="space-y-3">
          <div className="text-3xl">📸 🎥</div>
          <p className="text-gray-300">Drag & drop photos or videos here</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            Browse Files
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          {uploads.map((upload, idx) => (
            <div key={idx} className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-sm truncate max-w-[200px]">{upload.file.name}</span>
                <span className={`text-sm px-2 py-1 rounded ${
                  upload.status === 'complete' ? 'bg-green-600' :
                  upload.status === 'error' ? 'bg-red-600' : 'bg-blue-600'
                } text-white`}>
                  {upload.status === 'complete' ? '✓' : upload.status === 'error' ? '✗' : `${upload.progress}%`}
                </span>
              </div>
              {upload.status === 'uploading' && (
                <div className="w-full bg-gray-600 rounded-full h-1.5">
                  <div 
                    className="bg-blue-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${upload.progress}%` }}
                  />
                </div>
              )}
              {upload.error && <p className="text-red-400 text-sm mt-1">{upload.error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ MEDIA GALLERY COMPONENT ============
const AttachedFiles: React.FC<{
  media: RoadmapJournalMedia[];
  onDelete: (id: string) => void;
  onReorder: (media: { id: string; display_order: number }[]) => void;
}> = ({ media, onDelete }) => {
  if (media.length === 0) {
    return (
      <div className="text-center py-8 text-white">
        <div className="text-3xl mb-2">📷</div>
        <p>No media uploaded yet</p>
      </div>
    );
  }

  const photos = media.filter(m => m.media_type === 'photo');
  const videos = media.filter(m => m.media_type === 'video');

  return (
    <div className="space-y-4">
      {/* Photos */}
      {photos.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-white mb-2">Photos ({photos.length})</h5>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group aspect-video rounded overflow-hidden bg-gray-700">
                <img src={photo.file_url} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => onDelete(photo.id)}
                  className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full text-sm
                           opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-white mb-2">Videos ({videos.length})</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {videos.map((video) => (
              <div key={video.id} className="relative group aspect-video rounded overflow-hidden bg-gray-700">
                {video.thumbnail_url ? (
                  <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-4xl">🎥</span>
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 bg-black/70 rounded-full flex items-center justify-center">
                    <span className="text-white">▶</span>
                  </div>
                </div>
                {video.duration && (
                  <div className="absolute bottom-1 right-1 bg-black/70 text-white text-sm px-1.5 py-0.5 rounded">
                    {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                  </div>
                )}
                <button
                  onClick={() => onDelete(video.id)}
                  className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full text-sm
                           opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ ENTRY FORM COMPONENT ============
const EntryForm: React.FC<{
  entry?: RoadmapJournalEntry;
  onSave: (data: EntryFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}> = ({ entry, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState<EntryFormData>(
    entry ? {
      title: entry.title,
      description: entry.description || '',
      entry_date: String(entry.entry_date).slice(0, 10),
      is_published: entry.is_published,
      blocks: blocksForEntry(entry)
    } : defaultFormData
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white
                   focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
          placeholder="e.g., First Wash Plant Installation Complete"
        />
      </div>

      {/* Date */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Entry Date *</label>
        <input
          type="date"
          value={formData.entry_date}
          onChange={(e) => setFormData(prev => ({ ...prev, entry_date: e.target.value }))}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white
                   focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
        />
        <p className="text-sm text-gray-500 mt-1">
          Future dates will appear as "Upcoming Milestones"
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Page</label>
        <p className="text-sm text-gray-400 mb-3">Build the entry with text, photos, and videos from the media gallery.</p>
        <AnnouncementBuilder
          blocks={formData.blocks}
          onChange={(blocks) => setFormData((prev) => ({ ...prev, blocks }))}
          onError={(message) => alert(message)}
        />
      </div>

      {/* Published Toggle */}
      <div className="flex items-center gap-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_published}
            onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:ring-2 peer-focus:ring-yellow-400 rounded-full
                        peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5
                        after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5
                        after:transition-all peer-checked:bg-green-600"></div>
          <span className="ml-3 text-sm text-gray-300">
            {formData.is_published ? 'Published (visible to shareholders)' : 'Draft (hidden)'}
          </span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-600/50
                   text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          {loading ? 'Saving...' : entry ? 'Update Entry' : 'Create Entry'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// ============ ENTRY CARD COMPONENT ============
const EntryCard: React.FC<{
  entry: RoadmapJournalEntry;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePublish: () => void;
}> = ({ entry, onEdit, onDelete, onTogglePublish }) => {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const photoCount = entry.media?.filter(m => m.media_type === 'photo').length || 0;
  const videoCount = entry.media?.filter(m => m.media_type === 'video').length || 0;
  const isFuture = new Date(entry.entry_date) > new Date();

  return (
    <div className={`bg-gray-700 rounded-lg p-4 border-l-4 ${
      entry.is_published ? 'border-green-500' : 'border-gray-500'
    }`}>
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-sm px-2 py-0.5 rounded ${
              entry.is_published ? 'bg-green-600/20 text-green-400' : 'bg-gray-600 text-white'
            }`}>
              {entry.is_published ? 'Published' : 'Draft'}
            </span>
            {isFuture && (
              <span className="text-sm px-2 py-0.5 rounded bg-blue-600/20 text-blue-400">
                Future
              </span>
            )}
          </div>
          <h4 className="text-white font-semibold truncate">{entry.title}</h4>
          <p className="text-white text-sm">{formatDate(entry.entry_date)}</p>
          {entry.description && (
            <p className="text-gray-500 text-sm mt-1 line-clamp-2">{entry.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
            {photoCount > 0 && <span>📷 {photoCount} photos</span>}
            {videoCount > 0 && <span>🎥 {videoCount} videos</span>}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            onClick={onEdit}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
          >
            ✏️
          </button>
          <button
            onClick={onTogglePublish}
            className={`p-2 ${entry.is_published ? 'bg-gray-600 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}
                      text-white rounded text-sm transition-colors`}
            title={entry.is_published ? 'Unpublish' : 'Publish'}
          >
            {entry.is_published ? '📤' : '📥'}
          </button>
          <button
            onClick={onDelete}
            className="p-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN MANAGER COMPONENT ============
export const RoadmapJournalManager: React.FC = () => {
  const [entries, setEntries] = useState<RoadmapJournalEntry[]>([]);
  const [statistics, setStatistics] = useState<JournalStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit mode state
  const [editMode, setEditMode] = useState<'list' | 'create' | 'edit'>('list');
  const [selectedEntry, setSelectedEntry] = useState<RoadmapJournalEntry | null>(null);

  // Load entries and statistics
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [entriesData, statsData] = await Promise.all([
        RoadmapJournalService.getEntries({
          onlyPublished: false,
          includeMedia: true,
          orderBy: 'entry_date',
          orderDirection: 'desc'
        }),
        RoadmapJournalService.getStatistics()
      ]);

      setEntries(entriesData);
      setStatistics(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create new entry
  const handleCreate = async (data: EntryFormData) => {
    try {
      setSaving(true);
      await RoadmapJournalService.createEntry({
        title: data.title,
        entry_date: data.entry_date,
        is_published: data.is_published,
        blocks: data.blocks
      });
      await loadData();
      setEditMode('list');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create entry');
    } finally {
      setSaving(false);
    }
  };

  // Update existing entry
  const handleUpdate = async (data: EntryFormData) => {
    if (!selectedEntry) return;
    try {
      setSaving(true);
      await RoadmapJournalService.updateEntry({
        id: selectedEntry.id,
        title: data.title,
        entry_date: data.entry_date,
        is_published: data.is_published,
        blocks: data.blocks
      });
      await loadData();
      setEditMode('list');
      setSelectedEntry(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  // Delete entry
  const handleDelete = async (entry: RoadmapJournalEntry) => {
    if (!confirm(`Delete "${entry.title}"? This will also delete all associated media.`)) {
      return;
    }
    try {
      await RoadmapJournalService.deleteEntry(entry.id);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  };

  // Toggle publish status
  const handleTogglePublish = async (entry: RoadmapJournalEntry) => {
    try {
      await RoadmapJournalService.updateEntry({
        id: entry.id,
        is_published: !entry.is_published
      });
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update entry');
    }
  };

  // Delete media
  const handleDeleteMedia = async (mediaId: string) => {
    if (!confirm('Delete this media file?')) return;
    try {
      await RoadmapJournalService.deleteMedia(mediaId);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete media');
    }
  };

  // Edit entry
  const handleEdit = (entry: RoadmapJournalEntry) => {
    setSelectedEntry(entry);
    setEditMode('edit');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400" />
        <span className="ml-3 text-white">Loading journal entries...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <p className="text-red-400">⚠️ {error}</p>
        <button
          onClick={loadData}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">📖 Roadmap Journal Manager</h2>
          <p className="text-white">Each entry has a date, a title, and a page built from the media gallery.</p>
        </div>
        {editMode === 'list' && (
          <button
            onClick={() => setEditMode('create')}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg
                     font-medium transition-colors flex items-center gap-2"
          >
            <span>+</span> New Entry
          </button>
        )}
      </div>

      {/* Statistics Dashboard */}
      {editMode === 'list' && statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-white">{statistics.totalEntries}</div>
            <div className="text-sm text-white">Total Entries</div>
          </div>
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-400">{statistics.publishedEntries}</div>
            <div className="text-sm text-white">Published</div>
          </div>
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-blue-400">{statistics.totalPhotos}</div>
            <div className="text-sm text-white">Photos</div>
          </div>
          <div className="bg-gray-700 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-purple-400">{statistics.totalVideos}</div>
            <div className="text-sm text-white">Videos</div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {(editMode === 'create' || editMode === 'edit') && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editMode === 'create' ? '✨ Create New Entry' : '✏️ Edit Entry'}
          </h3>

          <EntryForm
            entry={editMode === 'edit' ? selectedEntry ?? undefined : undefined}
            onSave={editMode === 'create' ? handleCreate : handleUpdate}
            onCancel={() => { setEditMode('list'); setSelectedEntry(null); }}
            loading={saving}
          />

          {/* Media Management for existing entries */}
          {editMode === 'edit' && selectedEntry && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <h4 className="text-md font-semibold text-white mb-4">📎 Media Files</h4>

              {selectedEntry.media && selectedEntry.media.length > 0 ? (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-3">Files already attached to this entry. New photos and videos go in the page above.</p>
                  <AttachedFiles
                    media={selectedEntry.media}
                    onDelete={handleDeleteMedia}
                    onReorder={() => {}}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Entries List */}
      {editMode === 'list' && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">📋 All Entries</h3>

          {entries.length === 0 ? (
            <div className="text-center py-8 text-white">
              <div className="text-4xl mb-2">📝</div>
              <p>No journal entries yet. Create your first entry!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  onEdit={() => handleEdit(entry)}
                  onDelete={() => handleDelete(entry)}
                  onTogglePublish={() => handleTogglePublish(entry)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoadmapJournalManager;
